/**
 * Light / dark, as a class on <html>.
 *
 * shadcn's dark tokens live behind a `.dark` class rather than a media query, so
 * something has to set it. Following the system preference by default is the
 * only behaviour that needs no explanation; the override exists because a
 * reviewer comparing a scanned page against a figure often wants the page on a
 * light background regardless of what their OS is doing.
 *
 * The choice is stored, so it survives a reload. `system` is a real third state
 * rather than the absence of a setting: a user who picked "follow the system"
 * should keep following it when the system changes.
 */
export type Theme = 'light' | 'dark' | 'system'

const KEY = 'cm-theme'

export function storedTheme(): Theme {
  const v = localStorage.getItem(KEY)
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
}

export function applyTheme(theme: Theme): void {
  const dark =
    theme === 'dark'
    || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('dark', dark)
}

export function setTheme(theme: Theme): void {
  localStorage.setItem(KEY, theme)
  applyTheme(theme)
}

/**
 * Called once before React mounts, and again whenever the OS preference changes
 * — but only while the stored choice is `system`, or an explicit choice would be
 * silently overridden by the OS.
 */
export function initTheme(): void {
  applyTheme(storedTheme())
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (storedTheme() === 'system') applyTheme('system')
  })
}
